import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { DrawSVGPlugin } from 'gsap/DrawSVGPlugin'
import { MotionPathPlugin } from 'gsap/MotionPathPlugin'

gsap.registerPlugin(ScrollTrigger, DrawSVGPlugin, MotionPathPlugin)

export class Timeline {
    constructor() {
        if (!document.querySelector('.s-timeline')) return

        this._path = document.getElementById('path')
        this._draw = document.getElementById('draw')
        this._section = document.querySelector('.s-timeline')
        this._wrapper = this._section?.querySelector('.s-timeline-svg-wrapper')
        this._svg = this._path?.closest('svg')
        this._svgWidth = this._svg.getBoundingClientRect().width
        this._svgHeight = this._svg.getBoundingClientRect().height
        this._scrollDistance = 4500
        this._wrapperRect = null
        this._head = document.getElementById('head')
        this._header = document.querySelector('.h-wrap')
        this._sectionHeader = document.querySelector('.s-timeline-header')
        this._events = JSON.parse(this._section?.querySelector('[data-events]')?.getAttribute('data-events') ?? '[]').reverse()

        gsap.set(this._wrapper, {
            position: 'fixed',
            width: this._svgWidth,
            height: this._svgHeight,
            left: '50%',
            xPercent: 0,
            top: '50%',
            opacity: 0,
        })

        this.placeDatesOnPath()
        this.init()
    }

    _samplePath(step = 8) {
        if (!this._path) return []
        const totalLength = this._path.getTotalLength()
        const points = []
        for (let len = 0; len <= totalLength; len += step) {
            points.push(this._path.getPointAtLength(len))
        }
        return points
    }

    _getTangentAt(path, length, totalLength, delta = 2) {
        const t1 = Math.min(length + delta, totalLength)
        const t0 = Math.max(length - delta, 0)
        const p1 = path.getPointAtLength(t1)
        const p0 = path.getPointAtLength(t0)
        const dx = p1.x - p0.x
        const dy = p1.y - p0.y
        const norm = Math.hypot(dx, dy) || 1
        return { x: dx / norm, y: dy / norm }
    }

    _distToPath(q, pathPoints) {
        let min = Infinity
        for (let j = 0; j < pathPoints.length; j++) {
            const d = Math.hypot(q.x - pathPoints[j].x, q.y - pathPoints[j].y)
            if (d < min) min = d
        }
        return min
    }

    _getTextOffsetNormal(point, pathLength, pathPoints) {
        const tangent = this._getTangentAt(this._path, pathLength, this._path.getTotalLength())
        const N1 = { x: -tangent.y, y: tangent.x }
        const N2 = { x: tangent.y, y: -tangent.x }
        const testDist = 50
        const q1 = { x: point.x + N1.x * testDist, y: point.y + N1.y * testDist }
        const q2 = { x: point.x + N2.x * testDist, y: point.y + N2.y * testDist }
        const d1 = this._distToPath(q1, pathPoints)
        const d2 = this._distToPath(q2, pathPoints)
        return d1 >= d2 ? N1 : N2
    }

    placeDatesOnPath() {
        if (!this._path || !this._events?.length) return
        const totalLength = this._path.getTotalLength()
        const pathPoints = this._samplePath()
        let group = this._svg.querySelector('#timeline-dates')
        if (!group) {
            group = document.createElementNS('http://www.w3.org/2000/svg', 'g')
            group.id = 'timeline-dates'
            this._svg.appendChild(group)
        }
        group.innerHTML = ''
        const n = this._events.length
        this._dateStaggerProgress = []
        const offsetDate = 60
        this._events.forEach((event, i) => {
            const staggerStart = 5
            const staggerEnd = 0
            let staggerProgress
            if (n === 1) {
                staggerProgress = staggerStart
            } else {
                staggerProgress = staggerStart - ((staggerStart - staggerEnd) * i / (n - 1))
            }
            this._dateStaggerProgress.push(staggerProgress)
            const t = n > 1 ? i / (n - 1) : 0
            const pathLength = t * totalLength
            const point = this._path.getPointAtLength(pathLength)
            const N = this._getTextOffsetNormal(point, pathLength, pathPoints)
            const eventGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g')
            eventGroup.setAttribute('class', 'timeline-event')
            eventGroup.setAttribute('data-event-index', i)
            eventGroup.setAttribute('data-stagger-progress', staggerProgress)
            const ring = document.createElementNS('http://www.w3.org/2000/svg', 'circle')
            ring.setAttribute('cx', point.x)
            ring.setAttribute('cy', point.y)
            ring.setAttribute('r', 24)
            ring.setAttribute('class', 'timeline-date-ring')
            eventGroup.appendChild(ring)
            const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle')
            circle.setAttribute('cx', point.x)
            circle.setAttribute('cy', point.y)
            circle.setAttribute('r', 12)
            circle.setAttribute('fill', 'currentColor')
            circle.setAttribute('class', 'timeline-date-circle')
            eventGroup.appendChild(circle)
            const text = document.createElementNS('http://www.w3.org/2000/svg', 'text')
            const dateX = point.x + N.x * offsetDate
            const dateY = point.y + N.y * offsetDate
            text.setAttribute('x', dateX)
            text.setAttribute('y', dateY)
            text.setAttribute('text-anchor', 'middle')
            text.setAttribute('dominant-baseline', 'middle')
            text.textContent = event.date ?? ''
            text.setAttribute('class', 'timeline-date')
            eventGroup.appendChild(text)
            group.appendChild(eventGroup)
            const dateWidth = text.getComputedTextLength()
            const gap = 24
            const descWidth = 200
            let descX, descY
            const isAbove = Math.abs(N.y) >= Math.abs(N.x) && N.y < 0
            const isHorizontal = Math.abs(N.x) > Math.abs(N.y)
            if (Math.abs(N.y) >= Math.abs(N.x)) {
                if (N.y < 0) {
                    descX = dateX - dateWidth / 2
                    descY = dateY + gap
                } else {
                    descX = dateX - dateWidth / 2
                    descY = dateY + gap
                }
            } else {
                if (N.x < 0) {
                    descX = dateX - dateWidth / 2 - descWidth - gap
                    descY = dateY
                } else {
                    descX = dateX + dateWidth / 2 + gap
                    descY = dateY
                }
            }
            const foreignObject = document.createElementNS('http://www.w3.org/2000/svg', 'foreignObject')
            foreignObject.setAttribute('x', descX)
            foreignObject.setAttribute('y', descY)
            foreignObject.setAttribute('width', descWidth)
            foreignObject.setAttribute('class', 'timeline-date-description-wrapper')
            const descDiv = document.createElementNS('http://www.w3.org/1999/xhtml', 'div')
            descDiv.setAttribute('class', 'timeline-date-description')
            descDiv.textContent = (event.description ?? '').replace(/<br\s*\/?>/gi, '\n').trim()
            foreignObject.appendChild(descDiv)
            eventGroup.appendChild(foreignObject)
            const svgRect = this._svg.getBoundingClientRect()
            const viewBoxHeight = this._svg.viewBox?.baseVal?.height ?? 1796
            const descPixelHeight = descDiv.getBoundingClientRect().height
            const descHeightInUnits = (descPixelHeight / svgRect.height) * viewBoxHeight
            if (isAbove) {
                foreignObject.setAttribute('y', dateY - (gap + 24) - descHeightInUnits)
            } else if (isHorizontal) {
                foreignObject.setAttribute('y', dateY - descHeightInUnits / 2)
            }
        })
    }

    getDateStaggerProgress() {
        return this._dateStaggerProgress ?? []
    }

    init() {
        const self = this

        const tl = gsap.timeline({
            ease: 'none',
            scrollTrigger: {
                trigger: this._section,
                start: 'top top',
                end: `+=${this._scrollDistance}`,
                scrub: 0.3,
                pin: true,
                onEnter: function () {
                    if (self._header) {
                        self._header.style.opacity = '0';
                    }
                },
                onLeave: function () {
                    if (self._header) {
                        self._header.style.opacity = '1';
                    }
                },
                onEnterBack: function () {
                    if (self._header) {
                        self._header.style.opacity = '0';
                    }
                },
                onLeaveBack: function () {
                    if (self._header) {
                        self._header.style.opacity = '1';
                    }
                },
            },
        })

        tl.to(this._sectionHeader, {
            scale: 2,
            opacity: 0,
        }, '>')

        tl.to(this._wrapper, {
            opacity: 1,
        }, '>')

        tl.to(this._head,
            {
                ease: 'none',
                duration: 5,
                motionPath: {
                    path: "#draw",
                    align: "#draw",
                    autoRotate: true,
                    alignOrigin: [0.5, 0.5],
                    start: 1,
                    end: 0
                }
            }, '>')

        tl.fromTo(this._draw, { drawSVG: '100% 100%' }, {
            ease: 'none',
            duration: 5,
            drawSVG: '0% 100%',
        }, '<')

        tl.from('.timeline-event', {
            duration: 0.05,
            autoAlpha: 0,
            stagger: function (index) {
                return self._dateStaggerProgress[index]
            }
        }, '<-=0.05')


        let povDelay = 1,
            pos = { x: this._svgWidth / 2, y: this._svgHeight / 2 },
            xSet = gsap.quickSetter(this._wrapper, "x", "px"),
            ySet = gsap.quickSetter(this._wrapper, "y", "px");

        gsap.ticker.add(() => {
            pos.x += (-gsap.getProperty(this._head, 'x') - pos.x) * povDelay;
            pos.y += (-gsap.getProperty(this._head, 'y') - pos.y) * povDelay;
            xSet(pos.x);
            ySet(pos.y);
        });

        window.onresize = () => {
            gsap.set(this._wrapper, {
                width: '100%',
                height: () => window.innerHeight * (this._scrollDistance),
            })
        };


    }
}
